import { Controller } from "@hotwired/stimulus"

// State Licenses "Add A State To Website" dropdown. Tiles for every licensed
// state are pre-rendered; states without a license are hidden placeholder
// tiles. The dropdown lists exactly those placeholders.
//
// Picking a state (or clicking an uploaded tile's Edit-details pencil) opens
// the License Details modal (see state_license_details_controller), which
// saves number + expiration + PDF in ONE request; the response Turbo-Stream-
// swaps the tile to its uploaded state. Deleting a license swaps the tile
// back to a hidden placeholder. A MutationObserver on the tiles container
// keeps the dropdown in sync with those swaps.
export default class extends Controller {
  static targets = ["select", "tiles", "tile"]

  connect() {
    this.observer = new MutationObserver(() => this.rebuild())
    this.observer.observe(this.tilesTarget, { childList: true })
    this.rebuild()
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
  }

  // change->state-license-picker#add
  add(event) {
    const code = event.target.value
    if (!code) return

    event.target.value = ""
    const tile = this.tileTargets.find((t) => t.dataset.stateCode === code)
    this._openDetailsModal(tile)
  }

  // click->state-license-picker#editDetails (uploaded tiles)
  editDetails(event) {
    const tile = event.target.closest("[data-state-license-picker-target='tile']")
    this._openDetailsModal(tile)
  }

  // The dropdown = states whose tiles are hidden placeholders (no license yet).
  rebuild() {
    const available = this.tileTargets
      .filter((tile) => tile.dataset.uploaded !== "true" && tile.classList.contains("d-none"))
      .map((tile) => ({ code: tile.dataset.stateCode, name: tile.dataset.stateName }))
      .sort((a, b) => a.name.localeCompare(b.name))

    const select = this.selectTarget
    const placeholder = this._newOption("", "Select a licensed state…")
    select.replaceChildren(placeholder, ...available.map(({ code, name }) => this._newOption(code, name)))
  }

  _openDetailsModal(tile) {
    if (!tile) return
    // A persisted license (updateUrl present) pre-fills from its data
    // attributes; a placeholder opens blank.
    const persisted = Boolean(tile.dataset.updateUrl)
    document.dispatchEvent(new CustomEvent("state-license-details:open", {
      detail: {
        code: tile.dataset.stateCode,
        name: tile.dataset.stateName,
        updateUrl: tile.dataset.updateUrl || null,
        pdfFilename: tile.dataset.pdfFilename || null,
        licenseNumber: persisted ? (tile.dataset.licenseNumber || "") : "",
        expiresOn: persisted ? (tile.dataset.expiresOn || "") : "",
        neverExpires: persisted ? tile.dataset.neverExpires === "true" : false,
      },
    }))
  }

  _newOption(value, label) {
    const option = document.createElement("option")
    option.value = value
    option.textContent = label
    return option
  }
}
